import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import { api as fieldApi } from "@/fieldops/lib/api";

/** Partial quantities are normal. What is received rolls the PO, stamps the FRS rows and lands
 *  on the budget — and shows up in the field material counts, not a second inventory. */
export default function ReceiveDialog({ project, po, onClose, onDone }) {
  const outstanding = po.lines.filter((l) => l.outstanding_qty > 0);
  const [qty, setQty] = useState(() => Object.fromEntries(
    outstanding.map((l) => [l.id, l.outstanding_qty])));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // FT-2 §7 — bin locations. OFF by default, so the dialog looks exactly as it did until the
  // admin turns them on in Settings > Purchasing.
  const [binCfg, setBinCfg] = useState({ enabled: false, bins: [] });
  const [bin, setBin] = useState({});
  const warehouseBound = po.ship_to !== "site";
  useEffect(() => {
    if (!warehouseBound) return;
    fieldApi.get("/warehouse/bins").then(({ data }) => setBinCfg(data)).catch(() => {});
  }, [warehouseBound]);

  const save = async () => {
    const lines = Object.entries(qty)
      .map(([line_id, q]) => ({ line_id, qty: Number(q) || 0, bin: (bin[line_id] || "").trim() }))
      .filter((l) => l.qty > 0);
    if (!lines.length) {
      toast.error("Enter what actually turned up");
      return;
    }
    setBusy(true);
    try {
      const res = await opsApi.poReceive(project.id, po.id, { lines, note: note.trim() });
      toast.success(`${po.po_number} is now ${res.purchase_order.status_label.toLowerCase()}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be received"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-po-receive-dialog">
        <h3 className="text-base font-semibold text-slate-900">Receive · {po.po_number}</h3>
        <p className="mt-1 text-xs text-slate-500">
          Enter what arrived. Anything short stays outstanding on the purchase order.
        </p>
        {po.ship_to === "site" ? (
          <p className="mt-2 rounded-md bg-sky-50 px-3 py-1.5 text-xs text-sky-900"
             data-testid="ops-po-receive-direct-ship">
            This purchase order ships to site, so what you record here becomes a direct-ship
            delivery for the foreman to verify and place. It never becomes shop stock.
          </p>
        ) : (
          <p className="mt-2 rounded-md bg-slate-50 px-3 py-1.5 text-xs text-slate-600"
             data-testid="ops-po-receive-on-hand">
            {po.ship_to_label || "Shipping to the warehouse"} — what you record goes on hand for
            this job, and anyone waiting on it gets a follow-up pull ticket automatically.
          </p>
        )}

        {outstanding.length === 0 ? (
          <p className="mt-4 text-sm text-slate-600">Everything on this PO is already received.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {outstanding.map((l) => (
              <div key={l.id} className="flex items-center gap-2 text-xs">
                <span className="w-24 shrink-0 font-mono">{l.item_number || "—"}</span>
                <span className="flex-1 truncate text-slate-700">{l.description}</span>
                <span className="text-slate-400">of {Number(l.outstanding_qty)}</span>
                <Input type="number" value={qty[l.id]} min={0} max={l.outstanding_qty}
                       onChange={(e) => setQty((q) => ({ ...q, [l.id]: e.target.value }))}
                       className="h-8 w-20 text-xs" data-testid={`ops-po-receive-qty-${l.id}`} />
                {binCfg.enabled && warehouseBound && (
                  <>
                    <Input list={`ops-bin-options-${po.id}`} value={bin[l.id] || ""}
                           onChange={(e) => setBin((b) => ({ ...b, [l.id]: e.target.value }))}
                           placeholder="bin"
                           className="h-8 w-28 text-xs" data-testid={`ops-po-receive-bin-${l.id}`} />
                    <datalist id={`ops-bin-options-${po.id}`}>
                      {(binCfg.bins || []).map((b) => <option key={b} value={b} />)}
                    </datalist>
                  </>
                )}
              </div>
            ))}
            <label className="block text-xs">
              <span className="text-slate-500">Note</span>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="Where it landed, damage, short shipment…"
                     className="mt-1 h-8 text-xs" data-testid="ops-po-receive-note" />
            </label>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}
                  data-testid="ops-po-receive-cancel">Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy || !outstanding.length}
                  data-testid="ops-po-receive-save">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Record what arrived
          </Button>
        </div>
      </div>
    </div>
  );
}
